import { FC, memo, useCallback, useEffect, useRef, useState } from 'react'
import axios, { AxiosError } from 'axios'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import { Template } from '@leadgen/models/client'
import templateForm from '../../../../forms/templateForm'
import { PdfEditor } from '../../../atoms/PdfEditor'
import useCallbackDebounced from '../../../../hooks/shared/useCallbackDebounced'
import { useSnackbar } from '../../../providers/SnackbarProvider'
import CircularProgress from '../../../atoms/CircularProgress'
import { useDispatch, useSelector } from '../../../../redux/hooks'
import { useActiveTemplate } from '../../../providers/ActiveTemplateProvider'
import authSelectors from '../../../../redux/modules/auth/selectors'
import templateActions from '../../../../redux/modules/template/actions'
import companyActions from '../../../../redux/modules/company/actions'

const TemplatePanel: FC<{
  onSave: () => void
  onClose: () => void
  defaultTemplateJSON?: any | null // null means create new template from blank canvas
}> = ({ onSave, onClose, defaultTemplateJSON }) => {
  const dispatch = useDispatch()
  const companyId = useSelector(authSelectors.getCompanyId) as string
  const { showError } = useSnackbar()
  const [submitError, setSubmitError] = useState<string>()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { activeTemplate, loading, error } = useActiveTemplate()
  const [loadingTemplateJSONError, setLoadingTemplateJSONError] = useState<string | undefined>()

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [templateJSON, setTemplateJSON] = useState<any | undefined>()
  const afterSaveRef = useRef(false)
  const mode =
    (defaultTemplateJSON || defaultTemplateJSON === null) && !activeTemplate ? 'create' : 'edit'
  const [loadingTemplateJSON, setLoadingTemplateJSON] = useState(mode === 'edit')
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const templateJSONAnnotations =
    defaultTemplateJSON || defaultTemplateJSON === null ? defaultTemplateJSON : templateJSON
  const setTemplateAfterCreate = useCallback(
    (template: Template) => {
      dispatch(templateActions.setTemplateAfterCreate(template))
      dispatch(
        companyActions.updateLastChangedTemplate({
          lastChangedTemplateVersion: template.version,
        }),
      )
    },
    [dispatch],
  )
  const updateTemplateAfterSave = useCallback(
    (version: number) => {
      dispatch(templateActions.updateTemplateAfterSave(version))
      dispatch(
        companyActions.updateLastChangedTemplate({
          lastChangedTemplateVersion: version,
        }),
      )
    },
    [dispatch],
  )
  useEffect(() => {
    if (activeTemplate?.templateJSONUrl && !afterSaveRef.current) {
      setLoadingTemplateJSON(true)
      axios
        .get(activeTemplate.templateJSONUrl)
        .then(response => {
          setTemplateJSON(response.data)
          setLoadingTemplateJSONError(undefined)
          setLoadingTemplateJSON(false)
        })
        .catch((error: AxiosError) => {
          setLoadingTemplateJSONError(error.message)
          setLoadingTemplateJSON(false)
        })
    }
  }, [activeTemplate?.templateJSONUrl])
  useEffect(() => {
    const displayError = error || loadingTemplateJSONError || submitError
    if (displayError) {
      showError(displayError)
    }
  }, [error, loadingTemplateJSONError, showError, submitError])
  const onSaveWrapped = useCallback(
    async (pdf: Blob, templateJSON: Blob) => {
      setSubmitting(true)
      if (mode === 'create') {
        await templateForm
          .onSubmitCreate({
            pdf,
            templateJSON,
            companyId,
          })
          .then(template => {
            afterSaveRef.current = true
            setSubmitting(false)
            setSubmitError(undefined)
            setTemplateAfterCreate(template)
            onSave()
          })
          .catch((error: Error) => {
            setSubmitting(false)
            setSubmitError(error.message)
          })
        return
      }
      if (mode === 'edit') {
        await templateForm
          .onSubmitUpdate({
            pdf,
            templateJSON,
            companyId,
          })
          .then(version => {
            afterSaveRef.current = true
            updateTemplateAfterSave(version)
            setSubmitting(false)
            setSubmitError(undefined)
            onSave()
          })
          .catch((error: Error) => {
            setSubmitting(false)
            setSubmitError(error.message)
          })
        return
      }
    },
    [companyId, mode, onSave, setTemplateAfterCreate, updateTemplateAfterSave],
  )
  const onCloseWrapped = useCallbackDebounced(
    (hasChanges: boolean) => {
      if (hasChanges) {
        setDialogOpen(true)
      } else {
        onClose()
      }
    },
    [onClose],
  )
  const onDialogAgree = useCallbackDebounced(() => {
    setDialogOpen(false)
    onClose()
  }, [onClose])
  const onDialogDisagree = useCallbackDebounced(() => {
    setDialogOpen(false)
  }, [])
  const showLoading = (loading || loadingTemplateJSON) && mode !== 'create'
  const showEditor = (!!templateJSONAnnotations || templateJSONAnnotations === null) && !showLoading
  return (
    <Box id="template-panel" sx={{ position: 'relative', display: 'flex', flex: 1 }}>
      {showEditor && (
        <PdfEditor
          saving={submitting}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          templateJSON={templateJSONAnnotations}
          onSave={onSaveWrapped}
          onClose={onCloseWrapped}
        />
      )}
      {showLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CircularProgress delay={200} />
        </Box>
      )}
      <Dialog open={dialogOpen} onClose={onDialogDisagree}>
        <DialogTitle id="alert-dialog-title">Exit without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have unsaved changes. Are you sure you want to exit?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onDialogDisagree}>No, stay</Button>
          <Button onClick={onDialogAgree} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default memo(TemplatePanel)
